import { AUTH_CONFIG, User } from './auth.config';
import { STATIC_AUTH_SYNC } from '../constants/routes';
import { STORAGE_KEYS } from '../constants/storageKeys';
import {
  IFRAME_SYNC_AFTER_LOAD_MS,
  IFRAME_SYNC_SAFETY_TIMEOUT_MS,
} from '../constants/timing';

/**
 * Open a short-lived hidden iframe and resolve once it loads or a safety
 * timeout fires. Used for cross-origin session sync — the iframe has
 * first-party access to its own origin's localStorage, which the parent
 * window cannot reach directly.
 */
function iframeSync(
  url: string,
  waitAfterLoadMs: number = IFRAME_SYNC_AFTER_LOAD_MS,
  safetyTimeoutMs: number = IFRAME_SYNC_SAFETY_TIMEOUT_MS
): Promise<void> {
  return new Promise((resolve) => {
    let settled = false;
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.setAttribute('aria-hidden', 'true');
    iframe.src = url;

    const finish = (): void => {
      if (settled) return;
      settled = true;
      try {
        document.body.removeChild(iframe);
      } catch {
        /* noop */
      }
      resolve();
    };

    iframe.onload = () => setTimeout(finish, waitAfterLoadMs);
    iframe.onerror = finish;
    setTimeout(finish, safetyTimeoutMs);

    document.body.appendChild(iframe);
  });
}

/** Tell the Vue ERP app to clear its session. */
export function syncLogoutToVueApp(): Promise<void> {
  if (!AUTH_CONFIG.VUE_APP_URL) return Promise.resolve();
  return iframeSync(
    `${AUTH_CONFIG.VUE_APP_URL}${STATIC_AUTH_SYNC.syncLogout}?t=${Date.now()}`
  );
}

/**
 * Mirror a React-started emulation into the Vue ERP. Loads Vue's
 * `start-emulation.html` in a hidden iframe, carrying the emulated identity as
 * URL params — that page has first-party access to Vue's own-origin
 * localStorage (which this React window cannot reach directly) and writes the
 * same emulation keys Vue uses. No-op when there's no Vue URL or no emulated
 * user id. Keys mirror `constants/storageKeys.ts` / the Vue ERP exactly.
 */
export function syncStartEmulationToVueApp(
  emulatedUserId: string,
  emulatedUserName?: string | null,
  emulatedUserDetail?: string | null
): Promise<void> {
  if (!AUTH_CONFIG.VUE_APP_URL || !emulatedUserId) return Promise.resolve();
  const url = new URL(AUTH_CONFIG.VUE_APP_URL);
  url.pathname = STATIC_AUTH_SYNC.startEmulation;
  url.searchParams.set('uid', emulatedUserId);
  if (emulatedUserName) url.searchParams.set('uname', emulatedUserName);
  if (emulatedUserDetail) url.searchParams.set('udetail', emulatedUserDetail);
  url.searchParams.set('t', String(Date.now()));
  return iframeSync(url.toString());
}

/** Tell the Vue ERP app to clear its emulation state. */
export function syncEndEmulationToVueApp(): Promise<void> {
  if (!AUTH_CONFIG.VUE_APP_URL) return Promise.resolve();
  return iframeSync(
    `${AUTH_CONFIG.VUE_APP_URL}${STATIC_AUTH_SYNC.endEmulation}?t=${Date.now()}`
  );
}

/** Tell the Vue ERP app to store this React-issued session. */
export function syncLoginToVueApp(
  token: string,
  user: User | null,
  refreshToken?: string | null
): Promise<void> {
  if (!AUTH_CONFIG.VUE_APP_URL || !token || !user) return Promise.resolve();
  const url = new URL(AUTH_CONFIG.VUE_APP_URL);
  url.pathname = STATIC_AUTH_SYNC.syncLogin;
  url.searchParams.set('token', token);
  if (refreshToken) url.searchParams.set('refreshToken', refreshToken);
  url.searchParams.set('user', encodeURIComponent(JSON.stringify(user)));
  // Forward the canonical backend user record (holds the `userId` UUID).
  // `user` carries only the OAuth profile for Google SSO, which has no
  // userId — without this the Vue side can't resolve the real user. Mirrors
  // Vue's `syncLoginToReactApp` (`auth.store.ts`), which sends it back.
  const userDetailRaw = localStorage.getItem(STORAGE_KEYS.userDetail);
  if (userDetailRaw) {
    url.searchParams.set('userDetail', encodeURIComponent(userDetailRaw));
  }
  url.searchParams.set('t', String(Date.now()));
  return iframeSync(url.toString());
}
